import type { TimeseriesDatapoint } from '../../../../../core/types/timeseries.js';
import { getUnitAsString } from '../../../../../core/utils/get-unit-as-string.js';
import type {
  TimeseriesBandDataPoint,
  TimeseriesBandSlotProps,
} from '../../../../types/timeseries-band.js';
import { TimeseriesVariantSlotProps } from '../../../../types/timeseries.js';
import { getDatapointKey } from '../../../../utils/get-datapoint-value.js';
import { getDatapointTime } from '../../../../utils/timeseries.js';

function getDatapointValue(
  datapoint: TimeseriesBandDataPoint | TimeseriesDatapoint,
): number {
  if ('value' in datapoint) {
    return datapoint.value;
  }
  return Math.max(datapoint.y0, datapoint.y1);
}

/**
 * Group the accumulation of values of datapoints by start timestamp.
 * @param timeseries - given (TimeseriesBandSlotProps | TimeseriesVariantSlotProps)[]
 * @param variant -
 * @returns - map of grouped values by timestamp, max values by unit and unique units
 */
export function groupValueByTimestamp(
  timeseries: (TimeseriesBandSlotProps | TimeseriesVariantSlotProps)[],
  nonStackedMap: {
    groupedValues: Map<string, number>;
    maxValuesByUnit: Map<string, number>;
    uniqueUnits: Map<string, boolean>;
  } = {
    groupedValues: new Map(),
    maxValuesByUnit: new Map(),
    uniqueUnits: new Map(),
  },
) {
  const { groupedValues, maxValuesByUnit, uniqueUnits } = nonStackedMap;

  for (const series of timeseries) {
    const unit = getUnitAsString(series.unit);

    if (uniqueUnits.has(unit)) {
      uniqueUnits.set(unit, false);
    } else {
      uniqueUnits.set(unit, true);
    }

    for (const datapoint of series.datapoints) {
      const timestamp = getDatapointTime(
        datapoint.start,
        datapoint.end,
        (datapoint as TimeseriesDatapoint).center,
      );

      const datapointValue = getDatapointValue(datapoint);
      const currentValue = Math.abs(datapointValue);

      const maxUntilNow = maxValuesByUnit.get(unit) ?? -Infinity;
      if (currentValue > maxUntilNow) {
        maxValuesByUnit.set(unit, currentValue);
      }

      const datapointKey = getDatapointKey(timestamp, unit);

      const value = groupedValues.get(datapointKey) ?? 0;
      groupedValues.set(datapointKey, value + currentValue);
    }
  }

  return { groupedValues, maxValuesByUnit, uniqueUnits };
}
